import { useLocation } from "wouter";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { track } from "../../../lib/analytics";
import { useRoadmapItems } from "../../../lib/api/roadmap.api";

const POINTS = [
  {
    title: "Un projet personnel, devenu partagé",
    detail:
      "Palnia n'a pas été conçu pour être vendu. C'est un outil que j'ai construit pour moi, parce qu'aucune app existante ne me convenait. En le partageant, j'ai compris que le besoin était réel — alors j'ai décidé de l'ouvrir à tous.",
  },
  {
    title: "Vos données sont chiffrées",
    detail:
      "Tout est chiffré de bout en bout avec votre mot de passe, avant même de quitter votre navigateur. Personne ne peut lire vos données — même moi. C'est un choix de conception, pas une option.",
  },
  {
    title: "Construit avec soin, pas avec un budget marketing",
    detail:
      "Pas de growth hacks, pas de dark patterns. Les analytics servent uniquement à améliorer l'expérience, jamais à vous profiler. L'objectif c'est de faire un outil utile, pas addictif.",
  },
] as const;

export function WhySection() {
  const { data: roadmapItems } = useRoadmapItems();
  const allInProgress = roadmapItems?.filter((i) => i.status === "in-progress") ?? [];
  const allPlanned = roadmapItems?.filter((i) => i.status === "planned") ?? [];
  const inProgress = allInProgress.slice(0, 4);
  const planned = allPlanned.slice(0, 4);
  const inProgressRemaining = allInProgress.length - inProgress.length;
  const plannedRemaining = allPlanned.length - planned.length;
  const [, navigate] = useLocation();
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.15, "why");

  return (
    <section className="py-16 px-6 bg-surface-alt">
      <div
        ref={ref}
        className={`max-w-3xl mx-auto transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <h2 className="text-2xl md:text-3xl font-bold text-ink">
          Pourquoi Palnia existe
        </h2>
        <p className="mt-3 text-ink-muted leading-relaxed max-w-xl">
          Ce n'est pas un énième outil de productivité lancé par une startup.
          C'est un projet né d'un vrai besoin personnel — et il évolue
          grâce à vos retours.
        </p>

        <div className="mt-10 space-y-6">
          {POINTS.map((point, i) => (
            <div
              key={point.title}
              className="flex gap-4 transition-all duration-500"
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div
                className="mt-1 w-8 h-8 rounded-lg bg-accent/10 border border-accent/20
                  flex items-center justify-center shrink-0 text-sm font-bold
                  text-accent"
              >
                {i + 1}
              </div>
              <div>
                <h3 className="text-base font-semibold text-ink">
                  {point.title}
                </h3>
                <p className="mt-1 text-sm text-ink-muted leading-relaxed">
                  {point.detail}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex items-start gap-4 p-4 bg-surface rounded-xl border border-border">
          <div className="w-10 h-10 rounded-full bg-accent/10 border border-accent/20
            flex items-center justify-center shrink-0 text-sm font-bold text-accent">
            AJ
          </div>
          <div>
            <p className="text-sm text-ink leading-relaxed">
              Je suis <span className="font-semibold">Alane Jaunet</span>. Palnia
              c'est mon projet perso devenu public — je le construis en solo,
              avec la même exigence que pour mes clients.
            </p>
            <a
              href="https://pro.newalfox.fr"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("landing_external_link_clicked", { url: "https://pro.newalfox.fr" })}
              className="inline-block mt-2 text-xs font-medium text-accent hover:underline"
            >
              En savoir plus &rarr;
            </a>
          </div>
        </div>

        <div className="mt-10 p-5 bg-green-500/5 border border-green-500/15 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-green-500" stroke="currentColor" strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-ink">
                Vos données sont illisibles, même pour nous
              </h3>
              <p className="mt-1 text-sm text-ink-muted leading-relaxed">
                Tout ce que vous écrivez est verrouillé avec votre mot de passe avant d'être envoyé au serveur. Personne ne peut lire vos données — même nous.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Titres", "Notes", "Tags", "Sous-tâches", "Descriptions"].map((item) => (
                  <span key={item} className="flex items-center gap-1.5 text-xs text-green-400 font-medium">
                    <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <h3 className="text-lg font-semibold text-ink">
            Et ce n'est que le début.
          </h3>
          <p className="mt-1 text-sm text-ink-muted">
            Palnia évolue en continu grâce à vos retours.
          </p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-sm font-semibold text-amber-400">En cours</span>
              </div>
              <div className="flex flex-col gap-2">
                {inProgress.map((item) => (
                  <div key={item.title} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-amber-500/15 bg-amber-500/5">
                    <span className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400" />
                    <span className="text-sm text-ink">{item.title}</span>
                  </div>
                ))}
                {inProgressRemaining > 0 && (
                  <button onClick={() => { track("landing_roadmap_clicked", { source: "in_progress" }); navigate("/roadmap"); }} className="text-xs text-amber-400 hover:underline text-left px-2.5 pt-1">
                    +{inProgressRemaining} autre{inProgressRemaining > 1 ? "s" : ""} en cours →
                  </button>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-ink-faint/40" />
                <span className="text-sm font-semibold text-ink-faint">Prévu</span>
              </div>
              <div className="flex flex-col gap-2">
                {planned.map((item) => (
                  <div key={item.title} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-border bg-surface-alt/30">
                    <span className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-ink-faint/40" />
                    <span className="text-sm text-ink-muted">{item.title}</span>
                  </div>
                ))}
                {plannedRemaining > 0 && (
                  <button onClick={() => { track("landing_roadmap_clicked", { source: "planned" }); navigate("/roadmap"); }} className="text-xs text-ink-faint hover:underline text-left px-2.5 pt-1">
                    +{plannedRemaining} autre{plannedRemaining > 1 ? "s" : ""} prévu{plannedRemaining > 1 ? "s" : ""} →
                  </button>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={() => { track("landing_roadmap_clicked", { source: "cta" }); navigate("/roadmap"); }}
            className="mt-5 text-sm font-medium text-accent hover:underline"
          >
            Voir la roadmap complète &rarr;
          </button>
        </div>

      </div>
    </section>
  );
}
